import { useEffect, useMemo, useState } from 'react';
import {
    Datagrid,
    FunctionField,
    List,
    TopToolbar,
    useDataProvider,
    useListContext,
    useNotify,
    useRefresh,
} from 'react-admin';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReviewsIcon from '@mui/icons-material/Reviews';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const SOURCE_LABELS = {
    '2gis': '2ГИС',
    flamp: 'Фламп',
    yandex: 'Яндекс Карты',
    tbank: 'Т-Банк',
    tripadvisor: 'Tripadvisor',
    google: 'Google Maps',
    vk: 'ВКонтакте',
    manual: 'Вручную',
};

const STATUS_META = {
    new: { label: 'Новый', color: 'error' },
    in_progress: { label: 'В работе', color: 'warning' },
    resolved: { label: 'Обработан', color: 'success' },
};

const SENTIMENT_META = {
    positive: { label: 'Позитивный', color: 'success' },
    neutral: { label: 'Нейтральный', color: 'default' },
    negative: { label: 'Негативный', color: 'error' },
    mixed: { label: 'Смешанный', color: 'warning' },
};

const CATEGORY_LABELS = {
    animals: 'Животные', cleanliness: 'Чистота', navigation: 'Навигация',
    prices: 'Цены', staff: 'Персонал', food: 'Еда',
    infrastructure: 'Инфраструктура', safety: 'Безопасность', other: 'Другое',
};

const PRIORITY_META = {
    high: { label: 'Срочно', color: 'error' },
    normal: { label: 'Обычный', color: 'warning' },
    low: { label: 'Низкий', color: 'default' },
};

const SLA_META = {
    overdue: { label: 'SLA просрочен', color: 'error' },
    due_soon: { label: 'Срок скоро', color: 'warning' },
    on_track: { label: 'В срок', color: 'success' },
    resolved: { label: 'Выполнено', color: 'default' },
};

const FilterField = ({ label, children, sx }) => <Stack spacing={0.55} sx={{ minWidth: 0, ...sx }}>
    <Typography variant="caption" color="text.secondary" sx={{ pl: 0.25, fontWeight: 700, lineHeight: 1.2 }}>
        {label}
    </Typography>
    {children}
</Stack>;

const analysisExplanation = (record) => (record?.analysis_reasons || []).join('\n');

const parseCsvLine = (line, delimiter) => {
    const values = [];
    let value = '';
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
        const char = line[i];
        if (char === '"' && quoted && line[i + 1] === '"') { value += '"'; i += 1; }
        else if (char === '"') quoted = !quoted;
        else if (char === delimiter && !quoted) { values.push(value); value = ''; }
        else value += char;
    }
    values.push(value);
    return values;
};

const parseCsv = (content, defaultSource) => {
    const lines = content.replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) throw new Error('В файле нет строк с отзывами');
    const delimiter = (lines[0].match(/;/g) || []).length > (lines[0].match(/,/g) || []).length ? ';' : ',';
    const headers = parseCsvLine(lines[0], delimiter).map((item) => item.trim().toLowerCase());
    const aliases = {
        external_id: ['external_id', 'id', 'review_id'], author_name: ['author_name', 'author', 'name'],
        rating: ['rating', 'score', 'оценка'], text: ['text', 'review', 'отзыв', 'comment'],
        source_url: ['source_url', 'url', 'link'], published_at: ['published_at', 'date', 'дата'], source: ['source', 'источник'],
    };
    const column = (name) => aliases[name].map((alias) => headers.indexOf(alias)).find((index) => index >= 0);
    return lines.slice(1).map((line, index) => {
        const row = parseCsvLine(line, delimiter);
        const get = (name) => { const i = column(name); return i === undefined ? '' : (row[i] || '').trim(); };
        const rating = Number(get('rating'));
        if (!Number.isInteger(rating) || rating < 1 || rating > 5) throw new Error(`Строка ${index + 2}: оценка должна быть от 1 до 5`);
        return {
            source: get('source') || defaultSource,
            external_id: get('external_id') || `${defaultSource}-${index + 1}-${Date.now()}`,
            author_name: get('author_name') || null,
            rating,
            text: get('text') || null,
            source_url: get('source_url') || null,
            published_at: get('published_at') ? new Date(get('published_at')).toISOString() : null,
        };
    });
};

const ImportDialog = ({ open, onClose }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [source, setSource] = useState('2gis');
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const reviews = parseCsv(await file.text(), source);
            const { data } = await dataProvider.create('external-reviews-import', { data: { reviews } });
            notify(`Импорт завершён: новых ${data.created}, обновлено ${data.updated}`, { type: 'success' });
            refresh();
            onClose();
        } catch (error) {
            notify(`Не удалось импортировать: ${error.message}`, { type: 'error' });
        } finally { setBusy(false); }
    };

    return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Импорт отзывов</DialogTitle>
        <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    Загрузите CSV из кабинета площадки. Поддерживаются колонки: external_id, author_name, rating, text, source_url, published_at.
                </Typography>
                <TextField select label="Источник" value={source} onChange={(e) => setSource(e.target.value)}>
                    {Object.entries(SOURCE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                <Button component="label" variant="outlined" startIcon={<FileUploadIcon />}>
                    {file ? file.name : 'Выбрать CSV'}
                    <input hidden type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </Button>
            </Stack>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Отмена</Button><Button variant="contained" disabled={!file || busy} onClick={submit}>Импортировать</Button></DialogActions>
    </Dialog>;
};

const PublicSyncButton = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [busy, setBusy] = useState(false);

    const sync = async () => {
        setBusy(true);
        try {
            const { data } = await dataProvider.syncAllReviews();
            const sources = Object.entries(data.collected || {}).map(([name, count]) => `${SOURCE_LABELS[name] || name}: ${count}`).join(', ');
            const warning = Object.keys(data.errors || {}).length ? `; недоступны: ${Object.keys(data.errors).join(', ')}` : '';
            notify(`Сбор завершён: новых ${data.created}, обновлено ${data.updated}. ${sources}${warning}`, { type: warning ? 'warning' : 'success' });
            refresh();
        } catch (error) {
            notify(`Не удалось получить отзывы: ${error.message}`, { type: 'error' });
        } finally { setBusy(false); }
    };

    return <Button variant="outlined" startIcon={<SyncIcon />} disabled={busy} onClick={sync}>
        {busy ? 'Получаем отзывы…' : 'Собрать со всех площадок'}
    </Button>;
};

const ListActions = () => {
    const [open, setOpen] = useState(false);
    return <><TopToolbar><PublicSyncButton /><Button variant="contained" startIcon={<FileUploadIcon />} onClick={() => setOpen(true)}>Импорт CSV</Button></TopToolbar><ImportDialog open={open} onClose={() => setOpen(false)} /></>;
};

const EmptyReviews = () => {
    const [open, setOpen] = useState(false);

    return <Paper
        variant="outlined"
        sx={{ m: 2, p: { xs: 3, md: 5 }, borderRadius: 3, textAlign: 'center' }}
    >
        <Box
            sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
                borderRadius: 3,
                display: 'grid',
                placeItems: 'center',
                color: 'primary.main',
                bgcolor: 'action.selected',
            }}
        >
            <ReviewsIcon fontSize="large" />
        </Box>
        <Typography variant="h5" sx={{ mb: 1 }}>Отзывов пока нет</Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 620, mx: 'auto', mb: 2.5 }}>
            Загрузите первую выгрузку из кабинета площадки. После подключения API-коннекторов новые отзывы будут добавляться в эту ленту автоматически.
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
            {['2ГИС', 'Фламп', 'Яндекс Карты', 'Т-Банк', 'Tripadvisor'].map((label) => (
                <Chip key={label} label={label} size="small" variant="outlined" />
            ))}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
            <PublicSyncButton />
            <Button variant="contained" startIcon={<FileUploadIcon />} onClick={() => setOpen(true)}>Импортировать CSV</Button>
        </Stack>
        <ImportDialog open={open} onClose={() => setOpen(false)} />
    </Paper>;
};

const ReviewDetail = ({ open, onClose, record }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [status, setStatus] = useState(record?.status || 'new');
    const [response, setResponse] = useState(record?.response_text || '');
    const [assigneeId, setAssigneeId] = useState(record?.assignee_id || '');
    const [admins, setAdmins] = useState([]);
    const [duplicates, setDuplicates] = useState([]);
    const [viewCount, setViewCount] = useState(record?.view_count || 0);
    useEffect(() => {
        if (!open || !record) return;
        dataProvider.markReviewViewed(record.id)
            .then(({ data }) => { setViewCount(data.view_count); refresh(); })
            .catch(() => undefined);
        dataProvider.getList('admin-users', {
            pagination: { page: 1, perPage: 100 }, sort: { field: 'username', order: 'ASC' }, filter: {},
        }).then(({ data }) => setAdmins(data.filter((admin) => admin.is_active !== false))).catch(() => setAdmins([]));
        if (record.duplicate_count > 0) {
            dataProvider.getReviewDuplicates(record.id).then(({ data }) => setDuplicates(data)).catch(() => setDuplicates([]));
        }
    }, [dataProvider, open, record, refresh]);
    if (!record) return null;
    const save = async () => {
        try {
            await dataProvider.update('external-reviews', { id: record.id, data: { ...record, status, response_text: response, assignee_id: assigneeId || null } });
            notify('Отзыв обновлён', { type: 'success' }); refresh(); onClose();
        } catch (error) { notify(`Ошибка: ${error.message}`, { type: 'error' }); }
    };
    return <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>{SOURCE_LABELS[record.source] || record.source} · {'★'.repeat(record.rating)}</DialogTitle>
        <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <Typography variant="caption" color="text.secondary">{record.author_name || 'Анонимный автор'} · {record.published_at ? new Date(record.published_at).toLocaleString('ru-RU') : 'дата не указана'}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Tooltip arrow title={analysisExplanation(record)}><Chip size="small" label={(SENTIMENT_META[record.sentiment] || SENTIMENT_META.neutral).label} color={(SENTIMENT_META[record.sentiment] || SENTIMENT_META.neutral).color} /></Tooltip>
                    <Tooltip arrow title={analysisExplanation(record)}><Chip size="small" label={CATEGORY_LABELS[record.category] || 'Другое'} variant="outlined" /></Tooltip>
                    <Tooltip arrow title={analysisExplanation(record)}><Chip size="small" label={(PRIORITY_META[record.priority] || PRIORITY_META.normal).label} color={(PRIORITY_META[record.priority] || PRIORITY_META.normal).color} /></Tooltip>
                    {record.is_spam && <Chip size="small" label="Возможный спам" color="warning" />}
                    <Chip size="small" icon={<ScheduleIcon />} label={(SLA_META[record.sla_state] || SLA_META.on_track).label} color={(SLA_META[record.sla_state] || SLA_META.on_track).color} variant="outlined" />
                    <Chip size="small" icon={<VisibilityOutlinedIcon />} label={`Просмотрели: ${viewCount}`} variant="outlined" />
                    {record.duplicate_count > 0 && <Chip size="small" icon={<CallMergeIcon />} label={`Объединено: ${record.duplicate_count + 1}`} color="info" variant="outlined" />}
                </Stack>
                {!!record.analysis_reasons?.length && <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                        <InfoOutlinedIcon color="primary" sx={{ fontSize: 18 }} />
                        <Typography variant="subtitle2">Почему присвоены эти теги</Typography>
                    </Stack>
                    {record.analysis_reasons.map((reason) => <Typography key={reason} variant="body2" color="text.secondary" sx={{ '&::before': { content: '"•"', mr: 0.75 } }}>{reason}</Typography>)}
                </Paper>}
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{record.text || 'Текст отсутствует'}</Typography>
                {record.source_url && <Link href={record.source_url} target="_blank" rel="noreferrer">Открыть оригинал <OpenInNewIcon sx={{ fontSize: 14 }} /></Link>}
                <TextField select label="Статус" value={status} onChange={(e) => setStatus(e.target.value)}>{Object.entries(STATUS_META).map(([value, meta]) => <MenuItem key={value} value={value}>{meta.label}</MenuItem>)}</TextField>
                <TextField select label="Ответственный" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
                    <MenuItem value="">Не назначен</MenuItem>
                    {admins.map((admin) => <MenuItem key={admin.id} value={admin.id}>{admin.username}</MenuItem>)}
                </TextField>
                {record.sla_due_at && <Typography variant="caption" color={record.sla_state === 'overdue' ? 'error.main' : 'text.secondary'}>
                    Срок обработки: {new Date(record.sla_due_at).toLocaleString('ru-RU')}
                </Typography>}
                {duplicates.length > 1 && <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Одинаковый отзыв на площадках</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {duplicates.map((item) => <Chip key={item.id} size="small" label={SOURCE_LABELS[item.source] || item.source} component={item.source_url ? 'a' : 'div'} href={item.source_url || undefined} target={item.source_url ? '_blank' : undefined} clickable={!!item.source_url} />)}
                    </Stack>
                </Paper>}
                <TextField label="Черновик ответа / заметка" multiline minRows={4} value={response} onChange={(e) => setResponse(e.target.value)} helperText="Ответ сохраняется в админке. Публикация выполняется в кабинете источника." />
            </Stack>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Закрыть</Button><Button variant="contained" onClick={save}>Сохранить</Button></DialogActions>
    </Dialog>;
};

const ReviewGrid = () => {
    const [selected, setSelected] = useState(null);
    const { displayedFilters, filterValues, setFilters, total } = useListContext();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const [summary, setSummary] = useState(null);
    const [admins, setAdmins] = useState([]);

    useEffect(() => {
        dataProvider.getReviewSummary(7).then(({ data }) => setSummary(data)).catch(() => setSummary(null));
        dataProvider.getList('admin-users', {
            pagination: { page: 1, perPage: 100 }, sort: { field: 'username', order: 'ASC' }, filter: {},
        }).then(({ data }) => setAdmins(data)).catch(() => setAdmins([]));
    }, [dataProvider, total]);
    const adminNames = useMemo(() => Object.fromEntries(admins.map((admin) => [admin.id, admin.username])), [admins]);
    const updateFilter = (key, value) => {
        const next = { ...(filterValues || {}) };
        if (value === '' || value === null || value === undefined) delete next[key];
        else next[key] = value;
        setFilters(next, displayedFilters);
    };
    const updateDateFilter = (key, value, endOfDay = false) => {
        if (!value) return updateFilter(key, '');
        updateFilter(key, new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00'}`).toISOString());
    };
    const activeFilterCount = Object.values(filterValues || {}).filter((value) => value !== '' && value !== null && value !== undefined).length;

    const presets = [
        { label: 'Все отзывы', filters: {}, color: 'primary' },
        { label: 'Требуют внимания', filters: { priority: 'high' }, color: 'error', help: 'Срочные отзывы: оценка 1–2★ или тревожные слова о безопасности, травмах и жестоком обращении' },
        { label: 'Негативные', filters: { sentiment: 'negative' }, color: 'error' },
        { label: 'Новые', filters: { status: 'new' }, color: 'warning' },
        { label: 'Непрочитанные', filters: { unread_only: true }, color: 'info' },
        { label: 'Назначено мне', filters: { assigned_to_me: true }, color: 'primary' },
        { label: 'SLA просрочен', filters: { sla_state: 'overdue' }, color: 'error' },
        { label: 'Животные', filters: { category: 'animals' }, color: 'success' },
        { label: 'Цены', filters: { category: 'prices' }, color: 'warning' },
        { label: 'Чистота', filters: { category: 'cleanliness' }, color: 'info' },
        { label: 'Безопасность', filters: { category: 'safety' }, color: 'error' },
        { label: 'Возможный спам', filters: { is_spam: true }, color: 'warning' },
    ];
    const applyPreset = (filters) => setFilters(filters, displayedFilters);
    const presetIsActive = (filters) => {
        const current = Object.entries(filterValues || {}).filter(([, value]) => value !== '' && value !== null && value !== undefined);
        const desired = Object.entries(filters);
        return current.length === desired.length && desired.every(([key, value]) => filterValues?.[key] === value);
    };

    return <>
        {summary && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(6, minmax(110px, 1fr))' }, gap: 1, mb: 1 }}>
            {[
                { label: 'За 7 дней', value: summary.total },
                { label: 'Новые', value: summary.new, filters: { status: 'new' } },
                { label: 'Не читали', value: summary.unread_by_me, filters: { unread_only: true } },
                { label: 'Мои', value: summary.assigned_to_me, filters: { assigned_to_me: true } },
                { label: 'SLA просрочен', value: summary.overdue, filters: { sla_state: 'overdue' }, danger: summary.overdue > 0 },
                { label: 'Средняя оценка', value: summary.average_rating ? `${summary.average_rating} ★` : '—' },
            ].map((item) => <Paper
                key={item.label}
                component={item.filters ? 'button' : 'div'}
                type={item.filters ? 'button' : undefined}
                onClick={item.filters ? () => applyPreset(item.filters) : undefined}
                variant="outlined"
                sx={{ p: 1.25, borderRadius: 2.5, textAlign: 'left', bgcolor: item.danger ? 'error.50' : 'background.paper', cursor: item.filters ? 'pointer' : 'default', color: 'text.primary', font: 'inherit', '&:hover': item.filters ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {} }}
            ><Typography variant="h6" color={item.danger ? 'error.main' : 'text.primary'} sx={{ fontWeight: 800, lineHeight: 1.1 }}>{item.value}</Typography><Typography variant="caption" color="text.secondary">{item.label}</Typography></Paper>)}
        </Box>}
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 1, borderRadius: 3, boxShadow: 'none', overflow: 'visible' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <FilterAltOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Фильтры по отзывам</Typography>
                    {!!activeFilterCount && <Chip size="small" color="primary" label={`Выбрано: ${activeFilterCount}`} />}
                </Stack>
                <Button size="small" disabled={!activeFilterCount} onClick={() => setFilters({}, displayedFilters)} sx={{ textTransform: 'none' }}>Сбросить всё</Button>
            </Stack>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))', xl: 'repeat(7, minmax(0, 1fr))' },
                gap: 1.25,
                '& .MuiInputBase-root': { minHeight: 40 },
            }}>
                <FilterField label="Поиск по тексту и автору" sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                    <TextField size="small" placeholder="Введите текст" value={filterValues?.q || ''} onChange={(event) => updateFilter('q', event.target.value)} />
                </FilterField>
                <FilterField label="Источник">
                    <TextField select size="small" value={filterValues?.source || ''} onChange={(event) => updateFilter('source', event.target.value)}>
                        <MenuItem value="">Все источники</MenuItem>{Object.entries(SOURCE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                    </TextField>
                </FilterField>
                <FilterField label="Дата отзыва от">
                    <TextField size="small" type="date" value={filterValues?.published_from?.slice(0, 10) || ''} onChange={(event) => updateDateFilter('published_from', event.target.value)} />
                </FilterField>
                <FilterField label="Дата отзыва до">
                    <TextField size="small" type="date" value={filterValues?.published_to?.slice(0, 10) || ''} onChange={(event) => updateDateFilter('published_to', event.target.value, true)} />
                </FilterField>
                <FilterField label="Оценка">
                    <TextField select size="small" value={filterValues?.rating || ''} onChange={(event) => updateFilter('rating', event.target.value)}>
                        <MenuItem value="">Любая</MenuItem>{[1, 2, 3, 4, 5].map((value) => <MenuItem key={value} value={value}>{value} ★</MenuItem>)}
                    </TextField>
                </FilterField>
                <FilterField label="Тональность">
                    <TextField select size="small" value={filterValues?.sentiment || ''} onChange={(event) => updateFilter('sentiment', event.target.value)}>
                        <MenuItem value="">Любая</MenuItem>{Object.entries(SENTIMENT_META).map(([value, meta]) => <MenuItem key={value} value={value}>{meta.label}</MenuItem>)}
                    </TextField>
                </FilterField>
                <FilterField label="Тема">
                    <TextField select size="small" value={filterValues?.category || ''} onChange={(event) => updateFilter('category', event.target.value)}>
                        <MenuItem value="">Все темы</MenuItem>{Object.entries(CATEGORY_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                    </TextField>
                </FilterField>
                <FilterField label="Приоритет">
                    <TextField select size="small" value={filterValues?.priority || ''} onChange={(event) => updateFilter('priority', event.target.value)}>
                        <MenuItem value="">Любой</MenuItem>{Object.entries(PRIORITY_META).map(([value, meta]) => <MenuItem key={value} value={value}>{meta.label}</MenuItem>)}
                    </TextField>
                </FilterField>
                <FilterField label="Состояние обработки">
                    <TextField select size="small" value={filterValues?.status || ''} onChange={(event) => updateFilter('status', event.target.value)}>
                        <MenuItem value="">Все состояния</MenuItem>{Object.entries(STATUS_META).map(([value, meta]) => <MenuItem key={value} value={value}>{meta.label}</MenuItem>)}
                    </TextField>
                </FilterField>
                <FilterField label="Ответственный">
                    <TextField select size="small" value={filterValues?.assignee_id || ''} onChange={(event) => updateFilter('assignee_id', event.target.value)}>
                        <MenuItem value="">Все сотрудники</MenuItem>{admins.map((admin) => <MenuItem key={admin.id} value={admin.id}>{admin.username}</MenuItem>)}
                    </TextField>
                </FilterField>
                <FilterField label="Срок SLA">
                    <TextField select size="small" value={filterValues?.sla_state || ''} onChange={(event) => updateFilter('sla_state', event.target.value)}>
                        <MenuItem value="">Любой срок</MenuItem>{Object.entries(SLA_META).filter(([value]) => value !== 'resolved').map(([value, meta]) => <MenuItem key={value} value={value}>{meta.label}</MenuItem>)}
                    </TextField>
                </FilterField>
                <FilterField label="Прочитанность">
                    <TextField select size="small" value={filterValues?.unread_only ? 'unread' : ''} onChange={(event) => updateFilter('unread_only', event.target.value === 'unread' ? true : '')}>
                        <MenuItem value="">Все отзывы</MenuItem><MenuItem value="unread">Не прочитаны мной</MenuItem>
                    </TextField>
                </FilterField>
                <FilterField label="Проверка спама">
                    <TextField select size="small" value={filterValues?.is_spam === true ? 'spam' : filterValues?.is_spam === false ? 'clean' : ''} onChange={(event) => updateFilter('is_spam', event.target.value === 'spam' ? true : event.target.value === 'clean' ? false : '')}>
                        <MenuItem value="">Все отзывы</MenuItem><MenuItem value="clean">Не спам</MenuItem><MenuItem value="spam">Подозрительные</MenuItem>
                    </TextField>
                </FilterField>
            </Box>
        </Paper>
        <Paper variant="outlined" sx={{ width: '100%', maxWidth: '100%', minWidth: 0, borderRadius: 3, overflow: 'hidden', boxShadow: 'none' }}>
            <Box sx={{ px: 1.5, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', mr: 0.5 }}>Быстрые фильтры</Typography>
                    {presets.map((preset) => <Tooltip key={preset.label} arrow title={preset.help || ''}><Button
                        size="small"
                        color={preset.color}
                        variant={presetIsActive(preset.filters) ? 'contained' : 'outlined'}
                        onClick={() => applyPreset(preset.filters)}
                        aria-pressed={presetIsActive(preset.filters)}
                        sx={{ minHeight: 34, borderRadius: 2, px: 1.4, textTransform: 'none', whiteSpace: 'nowrap' }}
                    >{preset.label}</Button></Tooltip>)}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" variant="text" onClick={refresh} sx={{ textTransform: 'none' }}>Обновить</Button>
                    <Chip size="small" label={`${total || 0} отзывов`} />
                </Stack>
            </Box>
            <Box sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                maxHeight: { xs: 'calc(100dvh - 590px)', md: 'calc(100dvh - 480px)' },
                minHeight: 280,
                overflow: 'auto',
                '& .RaDatagrid-root': { minWidth: 1420, border: 0, borderRadius: 0 },
                '& .RaDatagrid-tableWrapper': { overflow: 'visible' },
                '& .RaDatagrid-table': { minWidth: 1420, tableLayout: 'fixed' },
                '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 3, bgcolor: 'background.paper', boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.10)', whiteSpace: 'nowrap' },
                '& .MuiTableCell-root': { py: 1.15, px: 1.25, verticalAlign: 'middle' },
                '& .RaDatagrid-row:hover': { bgcolor: 'action.hover' },
            }}>
                <Datagrid bulkActionButtons={false} rowClick={(_, __, record) => { setSelected(record); return false; }}>
                    <FunctionField label="Источник" sx={{ width: 125 }} render={(record) => <Chip size="small" label={SOURCE_LABELS[record.source] || record.source} variant="outlined" />} />
                    <FunctionField label="Оценка" sx={{ width: 105 }} render={(record) => <Typography color={record.rating <= 3 ? 'error.main' : 'warning.main'} sx={{ whiteSpace: 'nowrap' }}>{'★'.repeat(record.rating)}{'☆'.repeat(5 - record.rating)}</Typography>} />
                    <FunctionField label="Анализ" sx={{ width: 245 }} render={(record) => {
                        const sentiment = SENTIMENT_META[record.sentiment] || SENTIMENT_META.neutral;
                        return <Tooltip arrow title={analysisExplanation(record)}><Stack direction="row" spacing={0.5}><Chip size="small" label={sentiment.label} color={sentiment.color} /><Chip size="small" label={CATEGORY_LABELS[record.category] || 'Другое'} variant="outlined" /></Stack></Tooltip>;
                    }} />
                    <FunctionField label="Приоритет" sx={{ width: 105 }} render={(record) => { const meta = PRIORITY_META[record.priority] || PRIORITY_META.normal; return <Tooltip arrow title={analysisExplanation(record)}><Chip size="small" label={meta.label} color={meta.color} /></Tooltip>; }} />
                    <FunctionField label="Автор / дата" sx={{ width: 175 }} render={(record) => <Box sx={{ minWidth: 0 }}><Typography variant="body2" noWrap title={record.author_name || 'Аноним'}>{record.author_name || 'Аноним'}</Typography><Typography variant="caption" color="text.secondary">{record.published_at ? new Date(record.published_at).toLocaleDateString('ru-RU') : 'Дата не указана'}</Typography></Box>} />
                    <FunctionField label="Отзыв" sx={{ width: 330 }} render={(record) => <Typography variant="body2" title={record.text || ''} sx={{ overflow: 'hidden', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, whiteSpace: 'normal', lineHeight: 1.35 }}>{record.text || '—'}</Typography>} />
                    <FunctionField label="Ответственный / SLA" sx={{ width: 190 }} render={(record) => {
                        const meta = SLA_META[record.sla_state] || SLA_META.on_track;
                        return <Box><Stack direction="row" spacing={0.5} alignItems="center"><PersonOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} /><Typography variant="body2" noWrap>{record.assignee_id ? adminNames[record.assignee_id] || 'Назначен' : 'Не назначен'}</Typography></Stack><Typography variant="caption" color={record.sla_state === 'overdue' ? 'error.main' : 'text.secondary'}>{meta.label}</Typography></Box>;
                    }} />
                    <FunctionField label="Статус" sx={{ width: 130 }} render={(record) => { const meta = STATUS_META[record.status] || STATUS_META.new; return <Stack spacing={0.4} alignItems="flex-start"><Chip size="small" label={meta.label} color={meta.color} /><Typography variant="caption" color={record.viewed_by_me ? 'text.secondary' : 'info.main'}>{record.viewed_by_me ? `Просмотров: ${record.view_count}` : 'Не прочитан'}</Typography></Stack>; }} />
                </Datagrid>
            </Box>
        </Paper>
        <ReviewDetail key={selected?.id || 'empty'} open={!!selected} record={selected} onClose={() => setSelected(null)} />
    </>;
};

export const ExternalReviewsList = () => <Box className="reviews-page" sx={{ width: '100%', maxWidth: '100%', minWidth: 0, px: { xs: 0, md: 0.5 }, pt: { xs: 0.5, md: 1 }, overflow: 'hidden' }}>
    <List title="Отзывы" actions={<ListActions />} empty={<EmptyReviews />} sort={{ field: 'published_at', order: 'DESC' }} perPage={25} sx={{ width: '100%', maxWidth: '100%', minWidth: 0, '& .RaList-main': { width: '100%', maxWidth: '100%', minWidth: 0, p: 0 }, '& .RaList-content': { width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'visible', border: 0, boxShadow: 'none', bgcolor: 'transparent' } }}>
        <ReviewGrid />
    </List>
</Box>;
